'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { type ChangeEvent, type DragEvent, useRef, useState } from 'react';
import { IMAGE_UPLOAD_ACCEPT_ATTR } from '@/components/images/validation';
import { Icon } from '@/components/ui/icon';
import { Upload01Icon } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';
type Props = {
  onFileSelected: (file: File) => void;
  primaryText?: string;
  hintText?: string;
  aspectRatio?: 'square' | 'none';
  errorMessage?: string | null;
  className?: string;
  disabled?: boolean;
  'data-testid'?: string;
};
export function ImageUploadDropzone(t0) {
  const $ = _c(46);
  const {
    onFileSelected,
    primaryText,
    hintText,
    aspectRatio: t1,
    errorMessage,
    className,
    disabled: t2,
    "data-testid": t3
  } = t0;
  const aspectRatio = t1 === undefined ? "square" : t1;
  const disabled = t2 === undefined ? false : t2;
  const testId = t3 === undefined ? "image-upload-dropzone" : t3;
  const tImageUpload = useTranslations("image_upload");
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  let t4;
  if ($[0] !== primaryText || $[1] !== tImageUpload) {
    t4 = primaryText ?? tImageUpload("dropzone_text");
    $[0] = primaryText;
    $[1] = tImageUpload;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const primary = t4;
  let t5;
  if ($[3] !== hintText || $[4] !== tImageUpload) {
    t5 = hintText ?? tImageUpload("upload_hint");
    $[3] = hintText;
    $[4] = tImageUpload;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const hint = t5;
  let t6;
  if ($[6] !== disabled) {
    t6 = function handleClick() {
      if (disabled) {
        return;
      }
      fileInputRef.current?.click();
    };
    $[6] = disabled;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleClick = t6;
  let t7;
  if ($[8] !== disabled || $[9] !== onFileSelected) {
    t7 = function handleDrop(e) {
      e.preventDefault();
      setDragOver(false);
      if (disabled) {
        return;
      }
      const file = e.dataTransfer.files[0];
      if (file !== undefined) {
        onFileSelected(file);
      }
    };
    $[8] = disabled;
    $[9] = onFileSelected;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleDrop = t7;
  let t8;
  if ($[11] !== disabled) {
    t8 = function handleDragEnter(e_0) {
      e_0.preventDefault();
      if (disabled) {
        return;
      }
      setDragOver(true);
    };
    $[11] = disabled;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleDragEnter = t8;
  let t9;
  if ($[13] !== disabled) {
    t9 = function handleDragOver(e_1) {
      e_1.preventDefault();
      if (disabled) {
        return;
      }
      setDragOver(true);
    };
    $[13] = disabled;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const handleDragOver = t9;
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function handleDragLeave() {
      setDragOver(false);
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const handleDragLeave = t10;
  const t11 = dragOver ? "true" : "false";
  const t12 = aspectRatio === "square" && "aspect-square";
  const t13 = dragOver ? "border-border-primary bg-foreground-primary/5" : "border-border-primary hover:border-border-primary";
  const t14 = disabled && "cursor-not-allowed opacity-60";
  let t15;
  if ($[16] !== className || $[17] !== t12 || $[18] !== t13 || $[19] !== t14) {
    t15 = cn("flex w-full min-w-0 cursor-pointer flex-col items-center justify-center gap-3 rounded-control border-2 border-dashed px-6 py-8 text-center transition-colors", t12, t13, t14, className);
    $[16] = className;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Icon icon={Upload01Icon} className="size-6 shrink-0 text-foreground-secondary" />;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== primary) {
    t17 = <p className="text-body text-foreground-secondary">{primary}</p>;
    $[22] = primary;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== hint) {
    t18 = <p className="text-caption text-foreground-tertiary">{hint}</p>;
    $[24] = hint;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== disabled || $[27] !== handleClick || $[28] !== handleDragEnter || $[29] !== handleDragOver || $[30] !== handleDrop || $[31] !== primary || $[32] !== t11 || $[33] !== t15 || $[34] !== t17 || $[35] !== t18 || $[36] !== testId) {
    t19 = <button type="button" data-testid={testId} data-drag-active={t11} disabled={disabled} aria-label={primary} className={t15} onClick={handleClick} onDrop={handleDrop} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>{t16}{t17}{t18}</button>;
    $[26] = disabled;
    $[27] = handleClick;
    $[28] = handleDragEnter;
    $[29] = handleDragOver;
    $[30] = handleDrop;
    $[31] = primary;
    $[32] = t11;
    $[33] = t15;
    $[34] = t17;
    $[35] = t18;
    $[36] = testId;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== onFileSelected) {
    t20 = <input ref={fileInputRef} type="file" accept={IMAGE_UPLOAD_ACCEPT_ATTR} onChange={e_2 => {
      const file_0 = e_2.target.files?.[0];
      if (file_0 !== undefined) {
        onFileSelected(file_0);
      }
      if (fileInputRef.current !== null) {
        fileInputRef.current.value = "";
      }
    }} className="hidden" aria-hidden="true" tabIndex={-1} data-testid="image-upload-file-input" />;
    $[38] = onFileSelected;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== errorMessage) {
    t21 = errorMessage !== null && errorMessage !== undefined && <p role="alert" className="text-caption text-feedback-error-content">{errorMessage}</p>;
    $[40] = errorMessage;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t19 || $[43] !== t20 || $[44] !== t21) {
    t22 = <div className="flex w-full min-w-0 flex-col gap-2">{t19}{t20}{t21}</div>;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  return t22;
}